/**
 * Noibu Feature Flag: no announcement banners on mobile
 * (flag "2083-no-announcement-banners-on-mobile", Noibu A/B test 227).
 *
 * Structurally parallel to assets/noibu-flag-search.js. It is a separate file
 * on purpose: the other flag files define controls for running experiments and
 * must not change mid-flight.
 *
 * Both arms are server-rendered by Liquid. This script builds no DOM; it sets a
 * class on <html> that decides whether the already-rendered announcement bars
 * are visible (see sections/noibu-banners-ab.liquid). A flag failure is
 * therefore a no-op and the page renders exactly as it does today.
 *
 *   original                 -> html.nff-banners--a  (control, the default)
 *   no-announcement-banners  -> html.nff-banners--b
 *
 * Targeting (device type is mobile: phones and tablets) lives in the Noibu
 * test. Visitors outside it get the default ('original') and land on the
 * control. There is no CSS breakpoint: the flag decides who sees B.
 *
 * Noibu's implementation code for test 227 (console > A/B tests > 227 >
 * Implementation), implemented faithfully below in render():
 *
 *   function render(container) {
 *     const { variant } = client.getStringDetails(
 *       "2083-no-announcement-banners-on-mobile",
 *       "original"
 *     );
 *     switch (variant) {
 *       case "no-announcement-banners":
 *         // Variation B
 *         return renderNoAnnouncementBanners(container);
 *       case "original":
 *         // Variation A (control)
 *       default:
 *         // Users excluded by targeting will see this.
 *         return renderOriginal(container);
 *     }
 *   }
 *
 * The SDK's details object carries the arm in `variant` (and `value`); older
 * generated snippets destructured `variation`, which always fell through to the
 * control. readVariation() reads all three so neither shape can silently send
 * every B visitor the control.
 *
 * NO MID-PAGE SWITCH (CLS)
 * The arm on screen never changes after first paint. When the SDK answers
 * with a different arm than the one painted (in practice: a visitor's first
 * mobile page, before anything is cached), the answer is cached and applied
 * from the next page view by the gate's paint-time hint. Trade-off: a B
 * visitor's first page shows the control. Those page views carry
 * nff_no-banners-first-view = "deferred:<arm>" so they can be excluded when
 * reading the test.
 *
 * DEPLOY BEFORE STARTING THE TEST
 * While the test is a draft the SDK serves the default, so every visitor gets
 * the control and this file is inert. Shoppers bucketed into B before this code
 * is live are counted in B while seeing A, and that data cannot be repaired.
 */
(function () {
  'use strict';

  var FLAG = '2083-no-announcement-banners-on-mobile';
  var CONTROL = 'original';
  var VARIATION_B = 'no-announcement-banners';
  var CACHE_KEY = 'nff.no-banners';

  var REPORT_KEY = 'nff_no-banners';
  var STATE_ATTR = 'data-nff-banners-state';

  // Set (and reported as nff_no-banners-first-view) when the SDK's answer
  // differed from the arm already on screen and was held for the next page.
  var DEFERRED_ATTR = 'data-nff-banners-deferred';
  var FIRST_VIEW_KEY = 'nff_no-banners-first-view';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-banners--a';
  CLASSES[VARIATION_B] = 'nff-banners--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // NOIBUJS.addCustomAttribute comes from collect-core, which loads
    // independently of collect-ff, so reporting works even when flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function syncHeader() {
    // Dawn caches --header-height and the sticky header's bounds when the
    // header connects. One bar sits above the header and two below it, so
    // refresh both once the arm is applied, or the predictive search, the menu
    // drawer and the sticky reveal work from the old offsets.
    var header = document.querySelector('.section-header');
    if (header) {
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    var sticky = document.querySelector('sticky-header');
    if (sticky && typeof sticky.createObserver === 'function') {
      try {
        sticky.createObserver();
      } catch (error) {
        /* no-op: older Dawn builds without createObserver */
      }
    }
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    // Keep the shopper's place. Switching to B after first paint removes about
    // 300px of bars above <main> on a phone. At the very top of the page that
    // is the point of the test, but once the shopper has scrolled, the same
    // collapse would yank what they are reading upward, so scroll back by the
    // difference to hold it still.
    var main = document.getElementById('MainContent');
    var before = main && window.pageYOffset > 0 ? main.getBoundingClientRect().top : null;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-banners', variation);

    if (before !== null) {
      var shift = main.getBoundingClientRect().top - before;
      if (shift) window.scrollBy(0, shift);
    }

    syncHeader();

    document.dispatchEvent(
      new CustomEvent('noibu:banners-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached (7 days) so a returning collector does not watch the bars
    // collapse on every page. The SDK stays the source of truth; this only
    // feeds the paint-time hint in the gate section. Wrapped because storage
    // throws in private mode and some embedded webviews.
    try {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ v: variation, t: Date.now() })
      );
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_banners=no-announcement-banners or =original
    try {
      var value = new URLSearchParams(window.location.search).get('nff_banners');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    // Older SDK builds without getStringDetails.
    return client.getStringValue(FLAG, CONTROL);
  }

  function paintedVariation() {
    // The arm the visitor is looking at right now. The gate's paint-time hint
    // sets data-nff-banners from a cached decision before the bars are
    // parsed; with no hint, the bars are showing, which is the control.
    var painted = root.getAttribute('data-nff-banners');
    return painted === VARIATION_B ? VARIATION_B : CONTROL;
  }

  function reportFirstView(value) {
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(FIRST_VIEW_KEY, value);
          return;
        }
      } catch (error) {
        /* collect-core still booting */
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function apply(variation) {
    // Never change the painted arm mid-page. The SDK only answers after
    // window.load, so on a visitor's first page (no cached arm) B used to
    // collapse ~300px of bars above content the shopper was already looking
    // at: mobile product-page CLS p75 0.19 in B vs 0.15 in control
    // (Oct 10-22). Now a decision that differs from what is painted is only
    // cached; the paint-time hint applies it from the next page view, before
    // first paint, so nothing moves.
    remember(variation);

    if (variation === paintedVariation()) {
      activate(variation);
      return;
    }

    root.setAttribute(DEFERRED_ATTR, variation);
    reportFirstView('deferred:' + variation);
  }

  function renderNoAnnouncementBanners() {
    apply(VARIATION_B);
    return VARIATION_B;
  }

  function renderOriginal() {
    apply(CONTROL);
    return CONTROL;
  }

  function render(client) {
    var variant = readVariation(client);

    switch (variant) {
      case VARIATION_B:
        // Variation B
        return renderNoAnnouncementBanners();
      case CONTROL:
      // Variation A (control): falls through to the default.
      default:
        // Control, visitors excluded by targeting (desktop), and unresolved
        // flags all land here.
        return renderOriginal();
    }
  }

  var forced = forcedVariation();
  if (forced) {
    // Skips remember() and the SDK so a forced preview neither poisons the
    // visitor's cache nor records an exposure.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here: caching a value the SDK never produced would
      // replay a decision that was never made. A returning visitor keeps the
      // arm replayed from their earlier, real assignment so the bars do not
      // reappear mid-visit.
      if (!root.hasAttribute('data-nff-banners')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
